import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import { toCanonicalPath } from '@lib/utils';

const DEFAULT_TITLE = `${SITE_NAME} | FPL tips, stats and tools`;
const DEFAULT_DESCRIPTION =
  'Fantasy Premier League tips, stats, predictions and tools to help you win your mini-leagues.';
// The square brand logo is the only share image the site ships; crawlers
// accept it, though a purpose-built 1200x630 asset would render better.
const DEFAULT_SHARE_IMAGE = `${FFH_HOSTNAME}/images/FFHub.png`;

/**
 * Site-wide metadata fallbacks for `_app`'s head, so that every page carries a
 * title, description, canonical and Open Graph identity even when it has no SEO
 * source of its own. Before FFH-2443 a page with no Yoast head - `/tools`,
 * `/predicted-lineups`, `/search`, `/contributors` and a dozen others - shipped
 * an empty `<title>` and nothing else.
 *
 * Every tag here is designed to lose to a page-level one. `_app` renders before
 * the page, and next/head keeps the last of any duplicate pair, but what counts
 * as a duplicate varies by attribute, and all three rules matter here:
 *
 * - `<meta name>` de-duplicates by name, and only while the *earlier* tag is
 *   unkeyed, so the description here deliberately has no key.
 * - `<meta property>` does not de-duplicate by value at all: next/head's
 *   METATYPES list is name, httpEquiv, charSet and itemProp. Open Graph tags
 *   survive only as an exact key match, so every og tag in the codebase is
 *   keyed with its property, including the ones parsed out of Yoast's head.
 * - `<link>` has no intrinsic de-duplication either, so the canonical carries a
 *   key and page-level canonicals must carry the same one.
 *
 * `tests/lib/head-merge.test.tsx` runs this through the real next/head merge,
 * which is the only place those rules are actually observable.
 */
const getDefaultSEO = (pathname: string) => {
  const canonicalUrl = `${FFH_HOSTNAME}${toCanonicalPath(pathname)}`;

  return (
    <>
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <link rel="canonical" href={canonicalUrl} key="canonical" />
      <meta property="og:type" content="website" key="og:type" />
      <meta property="og:site_name" content={SITE_NAME} key="og:site_name" />
      <meta property="og:title" content={DEFAULT_TITLE} key="og:title" />
      <meta
        property="og:description"
        content={DEFAULT_DESCRIPTION}
        key="og:description"
      />
      <meta property="og:url" content={canonicalUrl} key="og:url" />
      <meta property="og:image" content={DEFAULT_SHARE_IMAGE} key="og:image" />
    </>
  );
};

/**
 * Sanity's quizPage carries one SEO field, an optional `metaDescription`, so a
 * funnel whose editor left it blank has nothing page-specific to say. These
 * resolve the head copy for every quiz-driven route from what the document does
 * provide, with a code-level fallback rather than the site-wide default, which
 * says nothing about the funnel a visitor actually landed on.
 */
const QUIZ_FALLBACK_DESCRIPTION =
  'Free Fantasy Premier League tools and data-led advice from Fantasy Football Hub, built to help you win your mini-leagues.';

/**
 * A single template literal, deliberately: next/head renders an empty `<title>`
 * when its children are an array, which is what emptied the title on the site's
 * largest organic page and on every `/quiz` route (FFH-2443).
 */
const toQuizPageTitle = (title: string) => `${title} | ${SITE_NAME}`;

const toQuizPageDescription = (metaDescription?: string) =>
  metaDescription?.trim() || QUIZ_FALLBACK_DESCRIPTION;

export {
  getDefaultSEO,
  toQuizPageTitle,
  toQuizPageDescription,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  QUIZ_FALLBACK_DESCRIPTION
};
